import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import React, { useState, useEffect, useRef } from "react";
import { countries } from "../../../utils/config";

export default function InputPhoneDisplay({
  label,
  name,
  type,
  id,
  placeholder,
  defaultValue,
  err,
  required,
  handleChange,
  disabled,
}) {
  const [selectedCountryCode, setSelectedCountryCode] = useState("+971");
  const [selectedCountryCode3, setSelectedCountryCode3] = useState("UAE");
  const [selectedValue, setSelectedValue] = useState("+971,UAE");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Searchable picker state (for country code)
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);

  // Prevent effect loop by remembering the last emitted value
  const lastEmittedRef = useRef("");

  // Initialize from defaultValue e.g. "(+971) 501234567"
  useEffect(() => {
    if (defaultValue) {
      const match = defaultValue.match(/\((.*?)\)\s?(.*)/);
      if (match && match.length === 3) {
        const code = match[1];
        const num = match[2];
        setSelectedCountryCode(code);
        setPhoneNumber(num);

        const found = countries.find((c) => c.mobile_code === code && c.code3);
        if (found) {
          setSelectedCountryCode3(found.code3);
          setSelectedValue(`${found.mobile_code},${found.code3}`);
        }
      }
    }
  }, [defaultValue]);

  // Emit combined value when code/number changes (exclude handleChange from deps!)
  useEffect(() => {
    const nextVal = `(${selectedCountryCode}) ${phoneNumber}`;
    if (nextVal === lastEmittedRef.current) return; // no-op if unchanged
    lastEmittedRef.current = nextVal;

    if (typeof handleChange === "function") {
      handleChange({ target: { name, value: nextVal } }, false);
    }
  }, [selectedCountryCode, phoneNumber, name]);

  // Filtered list for searchable overlay
  const filtered = countries
    .filter((c) => c.mobile_code && c.code3)
    .filter((c) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.mobile_code.toLowerCase().includes(q) ||
        (c.code3 || "").toLowerCase().includes(q) ||
        (c.code || "").toLowerCase().includes(q)
      );
    });

  const openPicker = () => {
    if (disabled) return;
    setIsPickerOpen(true);
    setQuery("");
    setHighlightIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closePicker = () => {
    setIsPickerOpen(false);
    setQuery("");
    setHighlightIndex(0);
  };

  // Close on outside click / ESC
  useEffect(() => {
    if (!isPickerOpen) return;
    const onClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) closePicker();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closePicker();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isPickerOpen]);

  const selectCountry = (c) => {
    const value = `${c.mobile_code},${c.code3}`;
    setSelectedValue(value);
    setSelectedCountryCode(c.mobile_code);
    setSelectedCountryCode3(c.code3);
    closePicker();
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  // Keyboard navigation in the picker
  const onPickerKeyDown = (e) => {
    if (!isPickerOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => {
        const ni = Math.min(i + 1, filtered.length - 1);
        scrollItemIntoView(ni);
        return ni;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => {
        const ni = Math.max(i - 1, 0);
        scrollItemIntoView(ni);
        return ni;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[highlightIndex];
      if (item) selectCountry(item);
    }
  };

  const scrollItemIntoView = (idx) => {
    const container = listRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-index="${idx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  };

  return (
    <div ref={wrapperRef}>
      {label && (
        <label
          htmlFor={name || id}
          className="block text-sm font-medium leading-6 text-gray-900 text-left"
        >
          {label}
          {required ? "*" : ""}
        </label>
      )}

      <div className="relative mt-2">
        <div className="flex border border-gray-300 rounded-md relative">
          {/* Country code "pill" (visual stays the same) */}
          <button
            type="button"
            disabled={disabled}
            className="whitespace-nowrap border-0 bg-blue-50 py-1.5 pl-3 pr-6 text-gray-900 shadow-sm ring-0 ring-inset ring-gray-300 sm:text-sm sm:leading-6 rounded-l-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            onClick={openPicker}
            aria-haspopup="listbox"
            aria-expanded={isPickerOpen}
            title="Change country code"
          >
            {`${selectedCountryCode} `}
          </button>

          {/* Phone input (unchanged UI) */}
          <input
            id={id}
            type="tel"
            className="flex-1 border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 rounded-r-md"
            placeholder={placeholder || "Phone number"}
            value={phoneNumber}
            name={name}
            onChange={handlePhoneNumberChange}
            disabled={disabled}
          />

          {err && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {err && <p className="text-left mt-2 text-sm text-red-600">{err}</p>}

        {/* Searchable overlay picker */}
        {isPickerOpen && (
          <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="p-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlightIndex(0);
                }}
                onKeyDown={onPickerKeyDown}
                placeholder="Search country or code…"
                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <ul
              ref={listRef}
              role="listbox"
              aria-label="Country codes"
              className="max-h-60 overflow-auto py-1"
              onKeyDown={onPickerKeyDown}
            >
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-500">No results</li>
              )}
              {filtered.map((c, idx) => {
                const isActive = idx === highlightIndex;
                return (
                  <li
                    key={`${c.code3}-${c.mobile_code}`}
                    data-index={idx}
                    role="option"
                    aria-selected={isActive}
                    className={`px-3 py-2 text-sm cursor-pointer ${
                      isActive ? "bg-indigo-50" : "hover:bg-gray-50"
                    }`}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()} // keep focus for Enter
                    onClick={() => selectCountry(c)}
                  >
                    <span className="font-medium">{c.name}</span>{" "}
                    <span className="text-gray-600">{c.mobile_code}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
